import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { WelcomeBonusProduct } from '../../../../@types/api';
import {
  AFFILIATE_BONUS_CATEGORY_OPTIONS,
  WELCOME_BONUS_CASINO_REWARD_TYPE_OPTIONS,
  WELCOME_BONUS_ELIGIBILITY_OPTIONS,
  WELCOME_BONUS_PRODUCT_TYPE_OPTIONS,
  WELCOME_BONUS_SPORTSBOOK_REWARD_TYPE_OPTIONS,
  WELCOME_BONUS_STATUS_OPTIONS,
  WELCOME_BONUS_TYPE_OPTIONS,
  WELCOME_BONUS_VALIDITY_PERIOD_OPTIONS,
  WELCOME_BONUS_WAGERING_MODE_OPTIONS,
} from '../../../../constants';
import {
  AFFILIATE_BONUS_CATEGORY,
  AFFILIATE_CAMPAIGN_PRODUCT,
  WELCOME_BONUS_WAGERING_MODE,
} from '../../../../constants/enums';
import type { WelcomeBonusCreateFormData } from '../../../../schema';
import FormNumberInput from '../../../molecules/FormNumberInput';
import FormSelect from '../../../molecules/FormSelect';
import { switchWelcomeBonusProduct } from './defaults';
import { CasinoCreateFields } from './products/casino';
import { SportsbookCreateFields } from './products/sportsbook';
import { WelcomeBonusFormSection } from './WelcomeBonusFormPrimitives';
import { WelcomeBonusAudienceCreateFields } from './WelcomeBonusAudienceFields';

const ProductSelect = () => {
  const { control, getValues, reset } = useFormContext<WelcomeBonusCreateFormData>();

  const handleProductChange = (event: SelectChangeEvent<WelcomeBonusProduct>) => {
    reset(switchWelcomeBonusProduct(getValues(), event.target.value as WelcomeBonusProduct));
  };

  return (
    <FormControl fullWidth required>
      <InputLabel id='select-product-label'>Product</InputLabel>
      <Controller
        control={control}
        name='product'
        render={({ field: { value } }) => (
          <Select
            labelId='select-product-label'
            id='select-product'
            label='Product'
            value={value}
            onChange={handleProductChange}
          >
            {WELCOME_BONUS_PRODUCT_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </FormControl>
  );
};

export const WelcomeBonusCampaignCreateFields = () => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<WelcomeBonusCreateFormData>();

  return (
    <WelcomeBonusFormSection title='Campaign'>
      <TextField
        error={!!errors.name}
        helperText={errors.name?.message}
        label='Name'
        {...register('name')}
        required
        autoFocus
      />
      <FormSelect
        label='Status'
        name='status'
        control={control}
        error={errors.status}
        options={WELCOME_BONUS_STATUS_OPTIONS}
        required
        allowDeselect={false}
      />
      <WelcomeBonusAudienceCreateFields />
      <ProductSelect />
      <TextField
        error={!!errors.description}
        helperText={errors.description?.message}
        label='Description'
        {...register('description')}
        multiline
      />
    </WelcomeBonusFormSection>
  );
};

export const WelcomeBonusEligibilityCreateFields = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<WelcomeBonusCreateFormData>();

  return (
    <WelcomeBonusFormSection title='Eligibility'>
      <FormSelect
        label='Eligibility criteria'
        name='eligibilityCriteria'
        control={control}
        error={errors.eligibilityCriteria}
        options={WELCOME_BONUS_ELIGIBILITY_OPTIONS}
        required
        allowDeselect={false}
      />
      <FormSelect
        label='Validity period'
        name='validityPeriod'
        control={control}
        error={errors.validityPeriod}
        options={WELCOME_BONUS_VALIDITY_PERIOD_OPTIONS}
        required
        allowDeselect={false}
      />
    </WelcomeBonusFormSection>
  );
};

export const WelcomeBonusRewardCreateFields = () => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<WelcomeBonusCreateFormData>();
  const product = useWatch({ control, name: 'product' });
  const bonusCategory = useWatch({ control, name: 'bonusCategory' });
  const isPercentageSelected = bonusCategory === AFFILIATE_BONUS_CATEGORY.PERCENTAGE;
  const isCasinoProduct = product === AFFILIATE_CAMPAIGN_PRODUCT.CASINO;
  const rewardTypeOptions = isCasinoProduct
    ? WELCOME_BONUS_CASINO_REWARD_TYPE_OPTIONS
    : WELCOME_BONUS_SPORTSBOOK_REWARD_TYPE_OPTIONS;

  return (
    <WelcomeBonusFormSection title='Reward'>
      <FormSelect
        label='Bonus type'
        name='bonusType'
        control={control}
        error={errors.bonusType}
        options={WELCOME_BONUS_TYPE_OPTIONS}
        required
        allowDeselect={false}
      />
      <FormSelect
        label='Awarded Bonus Type'
        name='rewardType'
        control={control}
        error={errors.rewardType}
        options={rewardTypeOptions}
        required
        allowDeselect={false}
        disabled={isCasinoProduct}
      />
      <Stack spacing={2}>
        <ToggleButtonGroup
          value={bonusCategory}
          onChange={(_event, value) =>
            value ? setValue('bonusCategory', value, { shouldDirty: true, shouldValidate: true }) : undefined
          }
          exclusive
          fullWidth
          aria-label='Bonus amount type'
        >
          {AFFILIATE_BONUS_CATEGORY_OPTIONS.map((option) => (
            <ToggleButton key={option.id} value={option.id}>
              {option.name}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {isPercentageSelected ? (
          <FormNumberInput
            key='bonusPercentage'
            error={errors.bonusPercentage}
            label='Bonus Percentage'
            control={control}
            name='bonusPercentage'
            allowDecimals={false}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position='end'>%</InputAdornment>,
              },
            }}
          />
        ) : (
          <FormNumberInput
            key='fixedAmount'
            error={errors.fixedAmount}
            label='Fixed Amount'
            control={control}
            name='fixedAmount'
            allowDecimals
            displayCurrency
          />
        )}
      </Stack>
      {isPercentageSelected ? (
        <FormNumberInput
          control={control}
          name='capLimit'
          label='Cap Limit'
          error={errors.capLimit}
          allowDecimals
          displayCurrency
          required
        />
      ) : null}
      <FormNumberInput
        control={control}
        name='minDepositAmount'
        label='Minimum Deposit Amount'
        error={errors.minDepositAmount}
        allowDecimals
        displayCurrency
        required
      />
      <FormNumberInput
        control={control}
        name='timeframe'
        label='Timeframe (days)'
        error={errors.timeframe}
        allowDecimals={false}
      />
    </WelcomeBonusFormSection>
  );
};

export const WelcomeBonusProductCreateFields = () => {
  const { control } = useFormContext<WelcomeBonusCreateFormData>();
  const product = useWatch({ control, name: 'product' });

  switch (product) {
    case AFFILIATE_CAMPAIGN_PRODUCT.SPORTSBOOK:
      return <SportsbookCreateFields />;
    case AFFILIATE_CAMPAIGN_PRODUCT.CASINO:
      return <CasinoCreateFields />;
  }
};

export const WelcomeBonusWageringCreateFields = () => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<WelcomeBonusCreateFormData>();
  const wageringMode = useWatch({ control, name: 'wageringMode' });
  const isFixedAmountWagering = wageringMode === WELCOME_BONUS_WAGERING_MODE.FIXED_AMOUNT;

  return (
    <WelcomeBonusFormSection title='Rollover / Wagering'>
      <ToggleButtonGroup
        value={wageringMode}
        onChange={(_event, value) =>
          value ? setValue('wageringMode', value, { shouldDirty: true, shouldValidate: true }) : undefined
        }
        exclusive
        fullWidth
        aria-label='Wagering type'
      >
        {WELCOME_BONUS_WAGERING_MODE_OPTIONS.map((option) => (
          <ToggleButton key={option.id} value={option.id}>
            {option.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {isFixedAmountWagering ? (
        <FormNumberInput
          key='wageringRequirement'
          control={control}
          name='wageringRequirement'
          label='Wagering Requirement'
          error={errors.wageringRequirement}
          allowDecimals
          displayCurrency
          required
        />
      ) : (
        <FormNumberInput
          key='multiplier'
          control={control}
          name='multiplier'
          label='Multiplier X'
          error={errors.multiplier}
          allowDecimals={false}
        />
      )}
    </WelcomeBonusFormSection>
  );
};
